import type { Dictionary, Params } from "./types";

/**
 * G-85 — the pure lookup. No React, no `window`, no module state: it takes
 * the dictionaries it should read and returns a string, which is what makes
 * it testable under the root jest project (node environment).
 *
 * Resolution order for a key with a `count` param, given the plural category
 * `Intl.PluralRules` picks for the locale:
 *   1. `${key}_${category}` in the target dictionary
 *   2. `${key}_other` in the target dictionary
 *   3. the bare key in the target dictionary
 *   4–6. the same three in English
 *   7. the key itself — visible on screen, so a missing string is a bug
 *      somebody sees rather than a blank
 * Without a count only steps 3, 6 and 7 apply.
 */

const pluralRules = new Map<string, Intl.PluralRules>();

function pluralCategory(locale: string, count: number): Intl.LDMLPluralRule {
  let rules = pluralRules.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(locale);
    pluralRules.set(locale, rules);
  }
  return rules.select(count);
}

function lookup(
  dictionary: Dictionary | undefined,
  key: string,
  category: Intl.LDMLPluralRule | null,
): string | undefined {
  if (!dictionary) return undefined;
  if (category !== null) {
    const exact = dictionary[`${key}_${category}`];
    if (exact !== undefined) return exact;
    const other = dictionary[`${key}_other`];
    if (other !== undefined) return other;
  }
  return dictionary[key];
}

/**
 * `{{name}}` → params.name. A placeholder with no matching param is left AS
 * IS, so the gap shows on screen instead of rendering "undefined".
 */
export function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

export function translate(
  dictionary: Dictionary | undefined,
  english: Dictionary,
  locale: string,
  key: string,
  params?: Params,
): string {
  const count = params?.count;
  const category =
    typeof count === "number" ? pluralCategory(locale, count) : null;
  const template =
    lookup(dictionary, key, category) ??
    lookup(english, key, category === null ? null : pluralCategory("en", count as number)) ??
    key;
  return interpolate(template, params);
}
